import { useState } from 'react';
import { AdminLayout } from '../../components/Layout/AdminLayout';
import { PageHeader } from '../../components/common';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Avatar,
  TextField,
  InputAdornment,
  Stack,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  Typography,
} from '@mui/material';
import { DataGrid, GridColDef, GridToolbar } from '@mui/x-data-grid';
import {
  Add,
  Edit,
  Delete,
  Visibility,
  Business,
  Search,
  FileDownload,
  MoreVert,
  LocationOn,
  Receipt,
} from '@mui/icons-material';
import { useCounties, useDeleteCounty } from '../../hooks';
import { useNavigate } from 'react-router-dom';
import { colors, transitions, radius } from '../../theme/theme';
import { useSnackbar } from '../../contexts/SnackbarContext';

export const CountiesPage = () => {
  const navigate = useNavigate();
  const { showSnackbar } = useSnackbar();
  const [page, setPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedCounty, setSelectedCounty] = useState<any>(null);

  const { counties, isLoading } = useCounties(page);
  const deleteCounty = useDeleteCounty();

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, county: any) => {
    setAnchorEl(event.currentTarget);
    setSelectedCounty(county);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedCounty(null);
  };

  const handleDelete = async () => {
    if (!selectedCounty) return;

    if (window.confirm(`Are you sure you want to delete ${selectedCounty.name}?`)) {
      try {
        await deleteCounty.mutateAsync(selectedCounty.id);
        showSnackbar('County deleted successfully', 'success');
        handleMenuClose();
      } catch (error) {
        showSnackbar('Failed to delete county', 'error');
      }
    }
  };

  const handleView = () => {
    if (selectedCounty) {
      navigate(`/superadmin/counties/${selectedCounty.id}`);
      handleMenuClose();
    }
  };

  const handleEdit = () => {
    if (selectedCounty) {
      navigate(`/superadmin/counties/${selectedCounty.id}/edit`);
      handleMenuClose();
    }
  };

  const handleExportCounties = () => {
    if (!counties?.data) return;

    const csv = [
      ['County Name', 'Code', 'Office Location', 'Onboarded Date'].join(','),
      ...counties.data.map((county: any) =>
        [
          county.name,
          county.code,
          county.county_details?.office_location || 'N/A',
          county.county_details?.created_at
            ? new Date(county.county_details.created_at).toLocaleDateString()
            : 'N/A',
        ].join(',')
      ),
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `counties_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const columns: GridColDef[] = [
    {
      field: 'logo',
      headerName: 'Logo',
      width: 80,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Avatar
          src={params.row.county_details?.logo}
          sx={{
            bgcolor: colors.primary.main,
            width: 40,
            height: 40,
          }}
        >
          {!params.row.county_details?.logo && <Business sx={{ fontSize: 20 }} />}
        </Avatar>
      ),
    },
    {
      field: 'name',
      headerName: 'County Name',
      flex: 1,
      minWidth: 200,
      renderCell: (params) => (
        <Typography variant="body2" fontWeight={600}>
          {params.value}
        </Typography>
      ),
    },
    {
      field: 'code',
      headerName: 'Code',
      width: 120,
      renderCell: (params) => (
        <Chip
          label={params.value}
          size="small"
          variant="outlined"
          sx={{
            fontWeight: 600,
            borderColor: colors.primary.main,
            color: colors.primary.main,
          }}
        />
      ),
    },
    {
      field: 'office_location',
      headerName: 'Office Location',
      flex: 1,
      minWidth: 220,
      valueGetter: (value, row) => row.county_details?.office_location || 'Not specified',
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <LocationOn sx={{ fontSize: 16, color: colors.grey[500] }} />
          <Typography variant="body2" color="text.secondary">
            {params.value}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 120,
      renderCell: () => <Chip label="Active" color="success" size="small" />,
    },
    {
      field: 'created_at',
      headerName: 'Onboarded',
      width: 140,
      valueGetter: (value, row) => row.county_details?.created_at,
      renderCell: (params) =>
        params.value ? new Date(params.value).toLocaleDateString() : 'N/A',
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 100,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            handleMenuOpen(e, params.row);
          }}
          sx={{ color: colors.grey[600] }}
        >
          <MoreVert fontSize="small" />
        </IconButton>
      ),
    },
  ];

  const filteredCounties = counties?.data?.filter((county: any) =>
    county.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    county.code?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    county.county_details?.office_location?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Summary cards data
  const summaryCards = [
    {
      title: 'Total Counties',
      value: (counties as any)?.total || 0,
      icon: <Business sx={{ fontSize: 32 }} />,
      gradient: `linear-gradient(135deg, ${colors.primary.main} 0%, ${colors.primary.dark} 100%)`,
    },
    {
      title: 'Active Counties',
      value: counties?.data?.length || 0,
      icon: <Business sx={{ fontSize: 32 }} />,
      gradient: `linear-gradient(135deg, ${colors.success.main} 0%, ${colors.success.dark} 100%)`,
    },
  ];

  return (
    <AdminLayout>
      <Box>
        <PageHeader
          title="Counties Management"
          subtitle="Manage onboarded counties and their configurations"
          breadcrumbs={[
            { label: 'Dashboard', path: '/superadmin/dashboard' },
            { label: 'Counties' },
          ]}
          action={
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => navigate('/superadmin/counties/create')}
              sx={{
                background: colors.gradients.primary,
                boxShadow: colors.shadows.button,
                transition: transitions.base,
                '&:hover': {
                  background: colors.gradients.primaryHover,
                  boxShadow: colors.shadows.buttonHover,
                  transform: 'translateY(-1px)',
                },
              }}
            >
              Onboard County
            </Button>
          }
        />

        {/* Summary Cards */}
        <Box sx={{ display: 'flex', gap: 3, mb: 3 }}>
          {summaryCards.map((card, index) => (
            <Card
              key={index}
              elevation={0}
              sx={{
                flex: 1,
                background: card.gradient,
                color: 'white',
                borderRadius: 3,
                position: 'relative',
                overflow: 'hidden',
                boxShadow: colors.shadows.base,
                transition: transitions.base,
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  right: 0,
                  width: '120px',
                  height: '120px',
                  background: 'rgba(255, 255, 255, 0.1)',
                  borderRadius: '50%',
                  transform: 'translate(30%, -30%)',
                },
              }}
            >
              <CardContent sx={{ p: 3, position: 'relative', zIndex: 1 }}>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Box>
                    <Typography
                      variant="body2"
                      sx={{
                        opacity: 0.9,
                        mb: 1,
                        textTransform: 'uppercase',
                        letterSpacing: 0.5,
                        fontSize: '0.75rem',
                      }}
                    >
                      {card.title}
                    </Typography>
                    <Typography variant="h3" fontWeight={700}>
                      {card.value}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      bgcolor: 'rgba(255,255,255,0.2)',
                      borderRadius: 2,
                      p: 1.5,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    {card.icon}
                  </Box>
                </Box>
              </CardContent>
            </Card>
          ))}
        </Box>

        {/* Main Content Card */}
        <Card
          elevation={0}
          sx={{
            borderRadius: 2,
            overflow: 'visible',
            border: `1px solid ${colors.grey[200]}`,
            boxShadow: colors.shadows.base,
          }}
        >
          <CardContent sx={{ p: 3 }}>
            {/* Search and Actions Bar */}
            <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <TextField
                size="small"
                placeholder="Search counties..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search sx={{ color: colors.grey[500] }} />
                    </InputAdornment>
                  ),
                }}
                sx={{ width: 350 }}
              />
              <Stack direction="row" spacing={1}>
                <Button
                  variant="outlined"
                  startIcon={<FileDownload />}
                  onClick={handleExportCounties}
                  disabled={!counties?.data || counties.data.length === 0}
                >
                  Export
                </Button>
              </Stack>
            </Box>

            {/* DataGrid */}
            <DataGrid
              rows={filteredCounties || []}
              columns={columns}
              loading={isLoading}
              autoHeight
              disableRowSelectionOnClick
              initialState={{
                pagination: {
                  paginationModel: { pageSize: 10 },
                },
              }}
              pageSizeOptions={[5, 10, 25, 50]}
              sx={{
                border: 'none',
                borderRadius: 2,
                '& .MuiDataGrid-main': {
                  borderRadius: 2,
                },
                '& .MuiDataGrid-cell:focus': {
                  outline: 'none',
                },
                '& .MuiDataGrid-row': {
                  cursor: 'pointer',
                  transition: transitions.fast,
                  '&:hover': {
                    bgcolor: `${colors.primary.main}08`,
                  },
                },
                '& .MuiDataGrid-columnHeaders': {
                  bgcolor: colors.grey[50],
                },
                '& .MuiDataGrid-columnHeader:first-of-type': {
                  borderTopLeftRadius: '16px',
                },
                '& .MuiDataGrid-columnHeader:last-of-type': {
                  borderTopRightRadius: '16px',
                },
                '& .MuiDataGrid-footerContainer': {
                  borderBottomLeftRadius: '16px',
                  borderBottomRightRadius: '16px',
                },
              }}
              onCellClick={(params) => {
                if (params.field === 'actions') return;
                navigate(`/superadmin/counties/${params.id}`);
              }}
            />
          </CardContent>
        </Card>
      </Box>

      {/* Actions Menu */}
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
        <MenuItem onClick={handleView}>
          <ListItemIcon>
            <Visibility fontSize="small" sx={{ color: colors.primary.main }} />
          </ListItemIcon>
          <ListItemText>View Details</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleEdit}>
          <ListItemIcon>
            <Edit fontSize="small" sx={{ color: colors.warning.main }} />
          </ListItemIcon>
          <ListItemText>Edit County</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (selectedCounty) {
              navigate(`/superadmin/counties/${selectedCounty.id}/billing`);
              handleMenuClose();
            }
          }}
        >
          <ListItemIcon>
            <Receipt fontSize="small" sx={{ color: colors.primary.main }} />
          </ListItemIcon>
          <ListItemText>Manage Billing</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleDelete}>
          <ListItemIcon>
            <Delete fontSize="small" sx={{ color: colors.error.main }} />
          </ListItemIcon>
          <ListItemText>Delete County</ListItemText>
        </MenuItem>
      </Menu>
    </AdminLayout>
  );
};
